import React, {
    Fragment, useState, useEffect, useRef
} from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Text, Grid, Box, Icon
} from 'components/ui';
import Price from 'components/ProductPage/Price/Price';
import DisplayName from 'components/ProductPage/DisplayName/DisplayName';
import CallToAction from 'components/ProductPage/CallToAction';
import OnlineOnly from 'components/ProductPage/OnlineOnly/OnlineOnly';
import Info from 'components/ProductPage/Info';
import LayoutTop from 'components/ProductPage/LayoutTop/LayoutTop';
import localeUtils from 'utils/LanguageLocale';
import profileApi from 'services/api/profile';
import productActions from 'actions/ProductActions';
import bindingMethods from 'analytics/bindingMethods/pages/all/generalBindings';
import RwdProductPageEN from 'components/ProductPage/locales/RwdProductPage_en_US';
import RwdProductPageFR from 'components/ProductPage/locales/RwdProductPage_fr_CA';

const getText = localeUtils.getLocalisedDataFromResource(RwdProductPageEN, RwdProductPageFR);

const RewardProduct = ({ processSkuId }) => {
    const dispatch = useDispatch();
    const currentProduct = useSelector(state => state.page.product, shallowEqual);
    const preferredStoreInfo = useSelector(state => state.user.preferredStoreInfo, shallowEqual);
    const netPoints = useSelector(state => state.basket?.netBeautyBankPointsAvailable);
    const basketItems = useSelector(state => state.basket?.items, shallowEqual);
    const [isSkuReady, setIsSkuReady] = useState(false);

    useEffect(() => {
        processSkuId({
            get isSkuReady() {
                return isSkuReady;
            },
            set isSkuReady(val) {
                setIsSkuReady(val);
            }
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const prevNetPointsRef = useRef(netPoints);
    useEffect(() => {
        if (prevNetPointsRef.current !== netPoints) {
            bindingMethods.setUserPropsWithCurrentData();
            prevNetPointsRef.current = netPoints;
        }
    }, [netPoints]);

    const isFirstBasketRender = useRef(true);
    useEffect(() => {
        if (isFirstBasketRender.current) {
            isFirstBasketRender.current = false;

            return;
        }

        const productId = currentProduct.productId || currentProduct?.productDetails?.productId;
        profileApi.getUserSpecificProductDetails(productId).then(productData => {
            dispatch(productActions.updateCurrentUserSpecificProduct(productData));
        });
    }, [basketItems]); // eslint-disable-line react-hooks/exhaustive-deps

    const { currentSku, productDetails = {} } = currentProduct;
    const { type, biType, rewardSubType, listPrice } = currentSku;
    const aboutDescription = productDetails.longDescription || productDetails.shortDescription || currentProduct.shortDescription;
    const faqDescription = productDetails.suggestedUsage || currentProduct.suggestedUsage;

    return (
        <div className={!isSkuReady ? 'isDefault' : null}>
            <LayoutTop
                sku={currentSku}
                product={currentProduct}
                hideBreadcrumbs={true}
                content={{
                    top: (
                        <Fragment>
                            <DisplayName
                                product={{
                                    brand: productDetails.brand || currentProduct.brand,
                                    displayName: productDetails.displayName || currentProduct.displayName
                                }}
                            />
                            <Price
                                sku={{
                                    type,
                                    biType,
                                    rewardSubType,
                                    listPrice
                                }}
                            />
                        </Fragment>
                    ),
                    bottom: (
                        <Grid gap={[null, null, 5]}>
                            <Grid gap={2}>
                                {preferredStoreInfo && (
                                    <Box
                                        lineHeight='tight'
                                        backgroundColor='nearWhite'
                                        borderRadius={2}
                                        padding={[3, 4]}
                                    >
                                        <Grid
                                            lineHeight='tight'
                                            columns='1fr auto'
                                            alignItems='center'
                                        >
                                            <div>
                                                {getText(preferredStoreInfo.isRopisable ? 'reserveNotAvailable' : 'pickUpNotAvailable')}
                                                <Text
                                                    marginTop={1}
                                                    display='block'
                                                    color='gray'
                                                    fontSize='sm'
                                                >
                                                    {getText('pickUpNotOfferedForItem')}
                                                </Text>
                                            </div>
                                            <Icon
                                                css={{ alignSelf: 'flex-start' }}
                                                color='gray'
                                                name='store'
                                            />
                                        </Grid>
                                    </Box>
                                )}
                                {currentSku && currentSku.isOnlineOnly && <OnlineOnly />}
                            </Grid>
                            {isSkuReady && (
                                <CallToAction
                                    sku={currentSku}
                                    product={currentProduct}
                                />
                            )}
                        </Grid>
                    )
                }}
            />
            {isSkuReady && aboutDescription && (
                <Info
                    shouldTruncate={false}
                    title={getText('aboutTheProduct')}
                    currentSku={currentSku}
                    description={aboutDescription}
                    dataAt='about_the_product_title'
                />
            )}
            {isSkuReady && faqDescription && (
                <Info
                    title={getText('faq')}
                    description={faqDescription}
                />
            )}
        </div>
    );
};

export default wrapFunctionalComponent(RewardProduct, 'RewardProduct');
